import type { ComponentType } from 'react';
import ARRollForward from '../pages/Billing/ARRollForward';
import AdoptionReportPage from '../pages/AdoptionReportPage';
import CensusReport from '../pages/CensusReport';
import InsuranceEligibilityReport from '../pages/InsuranceEligibilityReport';
import LateStartsReport from '../pages/LateStartsReport';
import LupaRiskReport from '../pages/LupaRiskReport';
import MarketingReport from '../pages/MarketingReport';
import PatientDiagnosesReport from '../pages/PatientDiagnosesReport';
import RehospitalizationReport from '../pages/RehospitalizationReport';
import PastDueVisitsReport from '../pages/PastDueVisitsReport';
import PcrReport from '../pages/PcrReport';
import RecertificationTimingReport from '../pages/RecertificationTimingReport';
import ScheduleDeviationReport from '../pages/ScheduleDeviationReport';
import ScheduleReport from '../pages/ScheduleReport';
import UserOnboardingReport from '../pages/UserOnboardingReport';
import ActiveUsersReport from '../pages/ActiveUsersReport';
import WorklogReport from '../pages/WorklogReport';
import WoundStatusReport from '../pages/WoundStatusReport';

export type ReportCardBadge = 'Claims' | 'Documentation' | 'EMR';
export type ReportCardIcon = 'bar-chart' | 'file-bar-chart';

export interface ReportCatalogEntry {
  slug: string;
  name: string;
  description: string;
  modernPath: `/reports/${string}`;
  legacyPath?: `/reporting/${string}`;
  component: ComponentType;
  badge: ReportCardBadge;
  icon: ReportCardIcon;
}

const standardReport = (
  entry: Omit<ReportCatalogEntry, 'modernPath' | 'legacyPath'> & {
    slug: string;
  }
): ReportCatalogEntry => ({
  ...entry,
  modernPath: `/reports/${entry.slug}`,
  legacyPath: `/reporting/${entry.slug}`,
});

export const reportCatalog: readonly ReportCatalogEntry[] = [
  standardReport({
    slug: 'adoption',
    name: 'Clinician Adoption',
    description:
      'Pending visits and visits completed in or outside Roger by clinician, with the percent completed in Roger.',
    component: AdoptionReportPage,
    badge: 'Documentation',
    icon: 'bar-chart',
  }),
  standardReport({
    slug: 'census',
    name: 'Census Report',
    description:
      'Current patients with clinic, admission, discharge and episode dates, demographics and address.',
    component: CensusReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'patient-diagnoses',
    name: 'Patient Diagnoses',
    description:
      'Active patients with their primary and secondary diagnoses, payer, PDGM grouping and HIPPS details.',
    component: PatientDiagnosesReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  {
    slug: 'pcr',
    name: 'PCR Report',
    description:
      'Pre-Claim Review tracking numbers and comments for RCD episodes, with episode dates and status.',
    modernPath: '/reports/pcr',
    component: PcrReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  },
  {
    slug: 'ar-roll-forward',
    name: 'AR Roll-forward',
    description:
      'Opening AR, billed, payments, write-offs, payer variance, removed claims and closing AR by month, clinic and payer class, at expected revenue.',
    modernPath: '/reports/ar-roll-forward',
    component: ARRollForward,
    // The badge is the Reports page group: this card sits with the AR reports.
    badge: 'Claims',
    icon: 'file-bar-chart',
  },
  standardReport({
    slug: 'rehospitalization',
    name: 'Rehospitalization Report',
    description:
      'Hospital admissions recorded on completed Transfer OASIS, with the transfer date, reason, destination and episode status.',
    component: RehospitalizationReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'patient-roster',
    name: 'Patient Roster',
    description:
      'Weekly patient roster with triage, disaster and diagnosis codes, assigned clinician and physician.',
    component: () => null,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'schedule',
    name: 'Schedule Report',
    description:
      'Scheduled visits in a date range, with assigned clinician, discipline, visit type and status.',
    component: ScheduleReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'schedule-deviation',
    name: 'Schedule Deviation',
    description:
      'Visits documented on a date other than the scheduled date, with deviation days and assigned clinician.',
    component: ScheduleDeviationReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'late-starts',
    name: 'Late Starts',
    description:
      'Admissions where the SOC date falls after the admission start date, with the gap in days.',
    component: LateStartsReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'insurance-eligibility',
    name: 'Insurance Eligibility',
    description:
      'Primary coverage eligibility status for each active patient, from the latest eligibility check.',
    component: InsuranceEligibilityReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'worklog',
    name: 'Worklog Report',
    description:
      'Actions taken on tasks by user and date, with the patient, clinic and task target date.',
    component: WorklogReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'user-onboarding',
    name: 'User Onboarding Report',
    description:
      'Agency users with invite time, last login time and counts of tasks assigned and submitted.',
    component: UserOnboardingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'active-users',
    name: 'Active Users',
    description:
      'Active clinicians and staff by clinic/branch with Roger app account status and disciplines.',
    component: ActiveUsersReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'past-due-visits',
    name: 'Past Due Tasks',
    description:
      'Overdue visits, orders and miscellaneous tasks, with the target date, days past due and assigned clinician.',
    component: PastDueVisitsReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'marketing',
    name: 'Marketing Report',
    description:
      'Referrals received in a date range, with clinic, internal and external referral source and physician.',
    component: MarketingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'wound-status',
    name: 'Wound Status Report',
    description:
      'Wounds by patient with type, location, status and progression over the last 30 days and overall.',
    component: WoundStatusReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'lupa-risk',
    name: 'LUPA Risk Report',
    description:
      'Medicare episodes below their LUPA visit threshold, with scheduled visits and the difference for each period.',
    component: LupaRiskReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
  standardReport({
    slug: 'recertification-timing',
    name: 'Recertification Timing',
    description:
      'Every recertification OASIS visit with its episode day, marked pass when it falls on days 56 through 60.',
    component: RecertificationTimingReport,
    badge: 'EMR',
    icon: 'file-bar-chart',
  }),
];

export const pairedStandardReports = reportCatalog.filter(
  (
    report
  ): report is ReportCatalogEntry & { legacyPath: `/reporting/${string}` } =>
    report.legacyPath !== undefined
);
